/**
 * Pestaña «Facturación» de una organización (docs/FASE-6.md §8): facturas con su detalle, registro
 * de pagos, anulación, notas de crédito y perfil de facturación.
 *
 * Las acciones que mueven dinero solo se pintan para un operador `owner` o `billing`. **Ocultar un
 * botón no es un control de acceso**: el API rechaza igual a un `support` (§7.2); esto es para que
 * la pantalla no ofrezca lo que va a fallar.
 */
import type { BillingProfile, Invoice, InvoiceDetail, InvoiceStatus, PaymentMethod } from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { billingApi } from "../api/client";
import { useSession } from "../api/session";
import { Badge, toneForStatus } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";
import { formatCurrency, formatDate, formatDateTime } from "../lib/format";

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: "Borrador",
  open: "Emitida",
  paid: "Pagada",
  void: "Anulada",
  uncollectible: "Incobrable",
};

const DRAFT_REASON_LABEL: Record<string, string> = {
  seats_missing: "Falta indicar los asientos contratados en la suscripción por asientos.",
};

/** Saldo pendiente de una factura. Resta de textos: los importes nunca pasan por `number` sin razón. */
function saldo(invoice: Invoice): string {
  const total = Math.round(Number(invoice.total) * 100);
  const pagado = Math.round(Number(invoice.amountPaid) * 100);
  return ((total - pagado) / 100).toFixed(2);
}

function RegistrarPagoModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}): ReactElement {
  const [amount, setAmount] = useState(saldo(invoice));
  const [gatewayPaymentId, setGatewayPaymentId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const resultado = await billingApi.registerPayment(invoice.id, {
        amount,
        currency: invoice.currency,
        // Un reintento del formulario (o un doble clic) no puede cobrar dos veces: la clave lleva
        // la factura y el importe, así que repetir el mismo pago es idempotente de verdad.
        idempotencyKey: `consola:${invoice.id}:${amount}:${gatewayPaymentId || "manual"}`,
        ...(gatewayPaymentId === "" ? {} : { gatewayPaymentId }),
        ...(notes === "" ? {} : { notes }),
      });
      const reactivadas = resultado.cycle.reactivated;
      toast.success(
        reactivadas > 0
          ? `Pago registrado. ${String(reactivadas)} suscripción(es) reactivada(s) automáticamente.`
          : "Pago registrado.",
      );
      onDone();
      onClose();
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Registrar pago · ${invoice.number ?? "sin número"}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <p>
          Total {formatCurrency(invoice.total, invoice.currency)} · pagado{" "}
          {formatCurrency(invoice.amountPaid, invoice.currency)} · saldo {formatCurrency(saldo(invoice), invoice.currency)}
        </p>
        <Field label={`Importe (${invoice.currency})`}>
          {(props) => (
            <input {...props} required inputMode="decimal" pattern="\d+(\.\d{1,2})?" value={amount} onChange={(e) => setAmount(e.target.value)} />
          )}
        </Field>
        <Field label="Referencia de la pasarela (opcional)">
          {(props) => <input {...props} value={gatewayPaymentId} onChange={(e) => setGatewayPaymentId(e.target.value)} />}
        </Field>
        <Field label="Nota (opcional)">
          {(props) => <input {...props} value={notes} onChange={(e) => setNotes(e.target.value)} />}
        </Field>
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={saving}>
            {saving ? "Registrando…" : "Registrar pago"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NotaDeCreditoModal({
  invoice,
  orgId,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  orgId: string;
  onClose: () => void;
  onDone: () => void;
}): ReactElement {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(invoice.total);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      await billingApi.createCreditNote(orgId, { invoiceId: invoice.id, reason, lines: [{ description, amount }] });
      toast.success("Nota de crédito emitida.");
      onDone();
      onClose();
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Nota de crédito sobre ${invoice.number ?? "la factura"}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <p>
          La factura original <strong>no se modifica</strong>: la nota de crédito es otro documento que la rectifica.
        </p>
        <Field label="Concepto">
          {(props) => <input {...props} required maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <Field label={`Importe a acreditar (${invoice.currency})`}>
          {(props) => (
            <input {...props} required inputMode="decimal" pattern="\d+(\.\d{1,2})?" value={amount} onChange={(e) => setAmount(e.target.value)} />
          )}
        </Field>
        <Field label="Motivo">
          {(props) => <input {...props} required maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={saving}>
            {saving ? "Emitiendo…" : "Emitir nota de crédito"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DetalleFactura({ invoiceId }: { invoiceId: string }): ReactElement {
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);

  useEffect(() => {
    let vivo = true;
    void billingApi
      .getInvoice(invoiceId)
      .then((d) => {
        if (vivo) setDetail(d);
      })
      .catch((err: unknown) => {
        toast.error(toDisplayMessage(err));
      });
    return () => {
      vivo = false;
    };
  }, [invoiceId]);

  if (!detail) return <p>Cargando detalle…</p>;

  return (
    <div className="tab-panel">
      <h4>Líneas</h4>
      <DataTable
        columns={[
          { key: "descripcion", header: "Concepto", render: (l) => l.description },
          { key: "cantidad", header: "Cantidad", render: (l) => l.quantity },
          { key: "unitario", header: "P. unitario", render: (l) => formatCurrency(l.unitAmount, detail.currency) },
          { key: "importe", header: "Importe", render: (l) => formatCurrency(l.amount, detail.currency) },
          { key: "impuesto", header: "IGV", render: (l) => formatCurrency(l.taxAmount, detail.currency) },
        ]}
        rows={detail.lines}
        rowKey={(l) => l.id}
        emptyMessage="Esta factura no tiene líneas."
      />
      <h4>Pagos</h4>
      <DataTable
        columns={[
          { key: "fecha", header: "Fecha", render: (p) => formatDateTime(p.receivedAt) },
          { key: "estado", header: "Estado", render: (p) => <Badge tone={toneForStatus(p.status)}>{p.status}</Badge> },
          { key: "importe", header: "Importe", render: (p) => formatCurrency(p.amount, p.currency) },
          { key: "pasarela", header: "Pasarela", render: (p) => p.gatewayPaymentId ?? p.gateway },
          { key: "motivo", header: "Motivo del fallo", render: (p) => p.failureMessage ?? "—" },
        ]}
        rows={detail.payments}
        rowKey={(p) => p.id}
        emptyMessage="Todavía no se registró ningún pago."
      />
    </div>
  );
}

function PerfilDeFacturacion({ orgId, puedeEscribir }: { orgId: string; puedeEscribir: boolean }): ReactElement {
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [billingEmail, setBillingEmail] = useState("");
  const [dunningPaused, setDunningPaused] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void billingApi
      .getProfile(orgId)
      .then((p) => {
        setProfile(p);
        setBillingEmail(p.billingEmail ?? "");
        setDunningPaused(p.dunningPaused);
      })
      .catch(() => {
        // Sin perfil (404) no hay nada que mostrar todavía: es el estado normal de una organización
        // recién creada, no un error que merezca una alerta.
        setProfile(null);
      });
  }, [orgId]);

  async function guardar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const guardado = await billingApi.saveProfile(orgId, {
        billingEmail: billingEmail === "" ? null : billingEmail,
        dunningPaused,
      });
      setProfile(guardado);
      toast.success("Perfil de facturación guardado.");
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)}>
      <h4>Perfil de facturación</h4>
      <Field label="Correo de facturación">
        {(props) => (
          <input {...props} type="email" disabled={!puedeEscribir} value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} />
        )}
      </Field>
      <label className="checkbox-label" htmlFor="dunning-paused">
        <input
          id="dunning-paused"
          type="checkbox"
          disabled={!puedeEscribir}
          checked={dunningPaused}
          onChange={(e) => setDunningPaused(e.target.checked)}
        />
        Congelar la mora de esta organización (no entra en `past_due`; la reactivación sigue funcionando)
      </label>
      {profile && <p className="subtle">Última actualización: {formatDateTime(profile.updatedAt)}</p>}
      {puedeEscribir && (
        <button type="submit" className="button-primary" disabled={saving}>
          {saving ? "Guardando…" : "Guardar perfil"}
        </button>
      )}
    </form>
  );
}

export function OrganizationBilling({ orgId }: { orgId: string }): ReactElement {
  const { operator } = useSession();
  const puedeEscribir = operator?.role === "owner" || operator?.role === "billing";

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [pagando, setPagando] = useState<Invoice | null>(null);
  const [rectificando, setRectificando] = useState<Invoice | null>(null);
  const [anulando, setAnulando] = useState<Invoice | null>(null);

  const recargar = useCallback(async () => {
    setLoading(true);
    try {
      const [page, metodos] = await Promise.all([billingApi.listInvoices(orgId), billingApi.listPaymentMethods(orgId)]);
      setInvoices(page.items);
      setMethods(metodos.items);
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const columns: DataTableColumn<Invoice>[] = [
    { key: "numero", header: "Número", render: (i) => i.number ?? <em>sin emitir</em> },
    {
      key: "periodo",
      header: "Periodo",
      render: (i) => `${formatDate(i.periodStart)} – ${formatDate(i.periodEnd)}`,
    },
    { key: "estado", header: "Estado", render: (i) => <Badge tone={toneForStatus(i.status)}>{STATUS_LABEL[i.status]}</Badge> },
    { key: "total", header: "Total", render: (i) => formatCurrency(i.total, i.currency) },
    { key: "saldo", header: "Saldo", render: (i) => formatCurrency(saldo(i), i.currency) },
    { key: "vence", header: "Vence", render: (i) => formatDate(i.dueAt) },
    {
      key: "acciones",
      header: "",
      render: (i) => (
        <div className="button-row">
          <button type="button" className="button-secondary button-small" onClick={() => setAbierta(abierta === i.id ? null : i.id)}>
            {abierta === i.id ? "Ocultar" : "Detalle"}
          </button>
          {puedeEscribir && i.status === "open" && (
            <button type="button" className="button-primary button-small" onClick={() => setPagando(i)}>
              Registrar pago
            </button>
          )}
          {puedeEscribir && (i.status === "open" || i.status === "draft") && (
            <button type="button" className="button-danger button-small" onClick={() => setAnulando(i)}>
              Anular
            </button>
          )}
          {puedeEscribir && i.kind === "invoice" && i.number !== null && (
            <button type="button" className="button-secondary button-small" onClick={() => setRectificando(i)}>
              Nota de crédito
            </button>
          )}
        </div>
      ),
    },
  ];

  const borradoresConMotivo = invoices.filter((i) => i.status === "draft" && i.draftReason !== null);

  return (
    <div>
      {borradoresConMotivo.length > 0 && (
        <div className="notice">
          <strong>Hay {borradoresConMotivo.length} factura(s) sin emitir.</strong> El ciclo no inventa importes: hasta
          que se corrija lo que falta, no se emiten.
          <ul>
            {borradoresConMotivo.map((i) => (
              <li key={i.id}>
                {formatDate(i.periodStart)}: {DRAFT_REASON_LABEL[i.draftReason ?? ""] ?? i.draftReason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={invoices}
        rowKey={(i) => i.id}
        emptyMessage={loading ? "Cargando…" : "Esta organización todavía no tiene facturas."}
      />

      {abierta !== null && <DetalleFactura invoiceId={abierta} />}

      <h4>Métodos de pago</h4>
      <DataTable
        columns={[
          { key: "pasarela", header: "Pasarela", render: (m) => m.gateway },
          { key: "marca", header: "Marca", render: (m) => m.brand ?? "—" },
          { key: "final", header: "Terminación", render: (m) => (m.last4 === null ? "—" : `•••• ${m.last4}`) },
          {
            key: "caduca",
            header: "Caduca",
            render: (m) => (m.expMonth === null || m.expYear === null ? "—" : `${String(m.expMonth)}/${String(m.expYear)}`),
          },
          { key: "defecto", header: "Por defecto", render: (m) => (m.isDefault ? "Sí" : "No") },
        ]}
        rows={methods}
        rowKey={(m) => m.id}
        emptyMessage="No hay métodos de pago guardados. El alta la hace la pasarela, no la consola."
      />

      <PerfilDeFacturacion orgId={orgId} puedeEscribir={puedeEscribir} />

      {pagando && <RegistrarPagoModal invoice={pagando} onClose={() => setPagando(null)} onDone={() => void recargar()} />}
      {rectificando && (
        <NotaDeCreditoModal
          invoice={rectificando}
          orgId={orgId}
          onClose={() => setRectificando(null)}
          onDone={() => void recargar()}
        />
      )}
      {anulando && (
        <ConfirmDialog
          title={`Anular ${anulando.number ?? "la factura"}`}
          message="Una factura anulada no vuelve. Si ya estaba pagada, lo que corresponde es una nota de crédito."
          confirmLabel="Anular"
          onCancel={() => setAnulando(null)}
          onConfirm={async () => {
            try {
              await billingApi.voidInvoice(anulando.id, { reason: "Anulada desde la consola." });
              toast.success("Factura anulada.");
              await recargar();
            } catch (err) {
              toast.error(toDisplayMessage(err));
            } finally {
              setAnulando(null);
            }
          }}
        />
      )}
    </div>
  );
}
